import classNames from 'classnames';
import {
  type ComponentPropsWithoutRef,
  type ElementType,
  type MouseEvent,
  type ReactNode,
  isValidElement,
} from 'react';
import { TooltipBubble } from '../TooltipBubble';
import { TextButton, TextCaption } from '../Typography';

export interface NavigationCardProps<T extends ElementType> {
  /** HTML Element or React component to render; defaults to "div" */
  as?: T;
  /** Card title */
  title: string | ReactNode;
  /**  Card width */
  containerClass?: string;
  /** Card description */
  description?: string;
  /** click handler */
  onClick?: (event?: MouseEvent<HTMLDivElement>) => void;
  /** Image component to render */
  image?: ReactNode;
  /** Button/icon/element to render */
  action?: ReactNode;
  /** Displays the action element only on hover */
  actionOnHover?: boolean;
  /** Active state of the action. Use to keep it visible when `actionOnHover` is true and focus is not on the component (e.g. when a dropdown is open) */
  isActionActive?: boolean;
  /** Disabled state */
  disabled?: boolean;
  /** Smaller paddings and border radius*/
  small?: boolean;
  /** prop for data-ds-id */
  dataDsId?: string;
}

export const NavigationCard = <T extends ElementType = 'div'>({
  as,
  title,
  onClick,
  description,
  image,
  action,
  actionOnHover,
  disabled,
  isActionActive,
  containerClass,
  small,
  dataDsId,
  ...props
}: NavigationCardProps<T> &
  Omit<ComponentPropsWithoutRef<T>, keyof NavigationCardProps<T>>) => {
  const Component = as || 'div';

  return (
    <Component
      data-ds-id={dataDsId}
      onClick={onClick}
      className={classNames(
        `flex items-center justify-between cursor-pointer px-4.5 group border border-ds-natural-150 hover:shadow-ds-nav-tile relative transition-all bg-ds-accent3-100 ${
          containerClass || 'w-ds-sm-modal'
        }`,
        {
          'py-4.5 rounded-lg': !small,
          'py-4 rounded-md': small,
          'opacity-50 pointer-events-none select-none': disabled,
        }
      )}
      {...props}
    >
      <div className="flex items-center">
        {image && <div className="mr-3 max-w-[48px]">{image}</div>}
        <div>
          {isValidElement(title) ? (
            title
          ) : (
            <TooltipBubble
              title={title}
              placement="top-start"
            >
              <div className={`${action ? 'w-40' : 'w-60'} truncate`}>
                <TextButton
                  label={title}
                  color="text-ds-natural-600"
                />
              </div>
            </TooltipBubble>
          )}

          <TooltipBubble
            title={description}
            placement="top-start"
          >
            <div
              className={classNames(
                'truncate text-ds-natural-300',
                action ? 'w-40' : 'w-60'
              )}
            >
              <TextCaption
                label={description}
                ellipsis
              />
            </div>
          </TooltipBubble>
        </div>
      </div>
      {action && (
        <div className={classNames(`self-center ${small ? 'pl-2' : 'pl-3'}`)}>
          <div
            className={classNames('relative', {
              'invisible group-hover:visible': actionOnHover && !isActionActive,
              visible: isActionActive,
            })}
          >
            {action}
          </div>
        </div>
      )}
    </Component>
  );
};
